import { type UseFormReturn, Controller } from "react-hook-form";
import { FileUpload, type UploadedFile } from "@/components/ui/FileUpload";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { DatePicker } from "@/components/ui/DatePicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import type {
  IntakeFormData,
  DropdownConfig,
  CustomField,
} from "../hooks/useIntakeForm";
import { BodyMap, type InjuryMarker } from "./BodyMap";

interface AdditionalStepProps {
  form: UseFormReturn<IntakeFormData>;
  dropdowns: DropdownConfig | undefined;
  customFields: CustomField[];
  isLoadingCustomFields: boolean;
  personType: string;
}

export function AdditionalStep({
  form,
  dropdowns,
  customFields,
  isLoadingCustomFields,
  personType,
}: AdditionalStepProps) {
  const {
    register,
    control,
    watch,
    setValue,
  } = form;

  return (
    <div className="space-y-6">
      {/* Photo Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Client Photos</CardTitle>
        </CardHeader>
        <CardContent>
          <FileUpload
            label="Upload Photos"
            accept="image/*"
            multiple
            maxSizeMB={5}
            onUpload={async (file) => {
              // In real implementation this would upload to the server
              const previewUrl = URL.createObjectURL(file);
              const uploaded: UploadedFile = {
                id: crypto.randomUUID(),
                name: file.name,
                size: file.size,
                type: file.type,
                previewUrl,
              };
              // Store the first photo URL
              setValue("photoUrl", previewUrl);
              return uploaded;
            }}
          />
        </CardContent>
      </Card>

      {/* Injury Documentation / Body Map */}
      <Controller
        name="injuries"
        control={control}
        render={({ field }) => (
          <BodyMap
            value={(field.value as InjuryMarker[]) ?? []}
            onChange={field.onChange}
            isInjured={watch("isInjured") ?? "na"}
            onIsInjuredChange={(val) => setValue("isInjured", val)}
            conditionWorsened={watch("conditionWorsened") ?? "na"}
            onConditionWorsenedChange={(val) => setValue("conditionWorsened", val)}
          />
        )}
      />

      {/* Narrative/Memo */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Additional Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Narrative / Memo
            </label>
            <textarea
              {...register("narrativeMemo")}
              rows={6}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Additional narrative, notes, or memo..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Shelter-specific: Bed & Departure */}
      {personType === "shelter" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shelter Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Bed ID"
                placeholder="Bed assignment"
                {...register("bedId")}
              />
              <Controller
                name="expectedDepartureDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Expected Departure Date"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Fields */}
      {customFields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Agency Custom Fields</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingCustomFields ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="h-4 w-28 animate-pulse rounded bg-muted" />
                    <div className="h-9 w-full animate-pulse rounded-md bg-muted" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {customFields.map((field) => (
                  <CustomFieldInput
                    key={field.id}
                    field={field}
                    value={watch("customFields")?.[field.id]}
                    onChange={(val) => {
                      const current = watch("customFields") ?? {};
                      setValue("customFields", { ...current, [field.id]: val });
                    }}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Income Sources */}
      {personType !== "child" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Income Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              name="incomeSources"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {(dropdowns?.incomeSourceTypes ?? []).map((option) => {
                    const checked = (field.value ?? []).includes(option.value);
                    return (
                      <label
                        key={option.value}
                        className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors hover:bg-accent"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const current = field.value ?? [];
                            const next = checked
                              ? current.filter((v) => v !== option.value)
                              : [...current, option.value];
                            field.onChange(next);
                          }}
                          className="h-4 w-4 rounded accent-primary"
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </div>
              )}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dynamic custom field renderer
// ---------------------------------------------------------------------------

function CustomFieldInput({
  field,
  value,
  onChange,
}: {
  field: CustomField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  switch (field.type) {
    case "text":
      return (
        <Input
          label={field.label}
          required={field.required}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "textarea":
      return (
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            {field.label}
            {field.required && <span className="ml-0.5 text-destructive">*</span>}
          </label>
          <textarea
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      );
    case "select":
      return (
        <Select
          label={field.label}
          required={field.required}
          options={field.options ?? []}
          value={(value as string) ?? ""}
          onChange={(v) => onChange(v)}
          clearable
        />
      );
    case "checkbox":
      return (
        <label className="flex items-center gap-2 pt-6 text-sm">
          <input
            type="checkbox"
            checked={(value as boolean) ?? false}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded accent-primary"
          />
          {field.label}
          {field.required && <span className="text-destructive">*</span>}
        </label>
      );
    case "number":
      return (
        <Input
          label={field.label}
          type="number"
          required={field.required}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        />
      );
    case "date":
      return (
        <DatePicker
          label={field.label}
          required={field.required}
          value={(value as string) ?? ""}
          onChange={(v) => onChange(v)}
        />
      );
    default:
      return null;
  }
}
